import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FiservCardStatusEnum,
  NavigationLeftIcon,
  RawButton,
  SuccessModal,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { Roles } from '@/common/constants';
import { ErrorModal, Modal } from '@/components';
import { SnackbarState } from '@/modules/core/core.types';
import { useHasAccess } from '@/modules/core/hooks';
import { useSnackbar } from '@/modules/core/hooks';
import { CardAccessModal } from '@/modules/transaction/components/Cards/components/CardAccessModal/CardAccessModal';
import useCardAccessModalStore from '@/modules/transaction/components/Cards/components/CardAccessModal/stores/useCardAccessModal.store.ts';
import { CardReplaceModal } from '@/modules/transaction/components/Cards/components/CardReplaceModal/CardReplaceModal';
import { ReplacementInitiationType } from '@/modules/transaction/components/Cards/components/CardReplaceModal/hooks/useCardReplacement.query';
import { LockUnlockCardModal } from '@/modules/transaction/components/Cards/components/LockUnlockCardModal';
import useLockUnlockCardModalStore from '@/modules/transaction/components/Cards/components/LockUnlockCardModal/stores/useLockUnlockCardModal.store';
import { ManageAccountAccessModal } from '@/modules/transaction/components/Cards/components/ManageAccountAccessModal';
import { useIsLockCardPurseAvailable } from '@/modules/transaction/components/Cards/hooks/useIsLockCardPurseAvailable';
import useCardReplaceModalStore from '@/modules/transaction/components/Cards/store/useCardReplaceModal.store.ts';
import { useSetCardStatusModal } from '@/modules/transaction/components/Cards/store/useSetCardStatusModal.store';
import { useChangeCardStatusQuery } from '@/modules/transaction/queries/useChangeCardStatus.query';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { useAuthStore } from '@/modules/user/stores';

import useGetCard from '../../hooks/useGetCard';
import CloseCardConfirmationModal from '../CloseCardConfirmationModal';

import CardActionButtons from './CardActionButtons';
import CardModalWrapper from './CardModalWrapper';

export const ChangeStatusCardModal: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { isMobile } = useResponsive();
  const isCardFlipDisabled = useIsLockCardPurseAvailable();
  const isEmployeeLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const {
    state: { cardId, visible: visibilityModal },
    handleReset,
  } = useSetCardStatusModal();
  const [closeCardConfirmation, setCloseCardConfirmation] = useState(false);
  const [closeCardSuccess, setCloseCardSuccess] = useState(false);
  const [isCardAccessModalVisible, setCardAccessModalVisible] = useState(false);
  const [isManageAccountAccessModalVisible, setManageAccountAccessModalVisible] = useState(false);
  const {
    state: { modalVisible: isLockUnlockCardModalVisible, successMessage: lockUnlockSuccessMessage },
    handleChangeVisibility: handleChangeLockUnlockCardModalVisibility,
    handleSetSuccessMessage: handleSetLockUnlockSuccessMessage,
  } = useLockUnlockCardModalStore();
  const { card, refetch, isLoading } = useGetCard({ cardId });
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { refetch: refetchCardList } = useGetCardsQuery(user?.employee?.id);
  const {
    handleReset: handleResetAccessModal,
    handleToggleCardLock,
    handleSetLockedPurses,
  } = useCardAccessModalStore();
  const { handleReset: handleResetLockUnlockCardModal } = useLockUnlockCardModalStore();

  const handleResetStores = useCallback(() => {
    handleResetAccessModal();
    handleResetLockUnlockCardModal();
    handleReset();
  }, [handleReset, handleResetAccessModal, handleResetLockUnlockCardModal]);

  const [hideVisibility, setHideVisibility] = useState(false);
  const visible = useMemo(
    () => (hideVisibility ? false : visibilityModal),
    [visibilityModal, hideVisibility]
  );
  const { mutateAsync: changeStatusMutation, isLoading: changeStatusMutationLoading } =
    useChangeCardStatusQuery();
  const loadingButton = useMemo(
    () => changeStatusMutationLoading || isLoading,
    [changeStatusMutationLoading, isLoading]
  );

  const {
    state: { modalVisible, cardholderName },
    handleChangeVisibility,
    handleChangeCardHolderName,
    handleChangeCardHolderID,
    handleChangeCardID,
    handleChangeCardStatus,
    handleChangeMailedDate,
    handleChangeActivationDate,
  } = useCardReplaceModalStore();
  const setCardInfo = useCallback(() => {
    handleChangeCardHolderName(card.holder.name);
    handleChangeCardHolderID(card.holder.dependentId);
    handleChangeCardID(card.id);
    handleChangeCardStatus(card.status);
    handleChangeMailedDate(card.mailedDate);
    handleChangeActivationDate(card.activationDate);
  }, [
    handleChangeCardHolderID,
    handleChangeCardHolderName,
    handleChangeCardStatus,
    handleChangeCardID,
    handleChangeMailedDate,
    handleChangeActivationDate,
    card,
  ]);

  const handleOpenCloseModal = useCallback(() => {
    setCloseCardConfirmation(true);
    setHideVisibility(true);
  }, []);

  const handleChangeCardAccessModalVisible = useCallback(() => {
    setCardAccessModalVisible((prev) => !prev);
    setHideVisibility?.(true);
  }, [setHideVisibility]);

  const handleOpenLockUnlockCardModal = useCallback(() => {
    handleChangeLockUnlockCardModalVisibility(true);
    setHideVisibility(true);
  }, [handleChangeLockUnlockCardModalVisibility]);

  const handleCloseLockUnlockCardModal = useCallback(() => {
    handleChangeLockUnlockCardModalVisibility(false);
    setHideVisibility(false);
  }, [handleChangeLockUnlockCardModalVisibility]);

  const handleOpenManageAccountAccessModal = useCallback(() => {
    setManageAccountAccessModalVisible(true);
    setHideVisibility(true);
  }, []);

  const handleCloseManageAccountAccessModal = useCallback(() => {
    setManageAccountAccessModalVisible(false);
    setHideVisibility(false);
  }, []);

  const handleManageAccountAccessSuccess = useCallback(() => {
    refetch();
    refetchCardList();
    setHideVisibility(false);
    handleResetStores();
    handleAddPermanentSnackbar({
      text: t('Account access has been updated!'),
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [refetch, refetchCardList, handleResetStores, handleAddPermanentSnackbar, t]);

  const handleLockUnlockCardSuccess = useCallback(
    (nextStatus: 'locked' | 'unlocked') => {
      refetch();
      refetchCardList();
      handleSetLockUnlockSuccessMessage(
        t(card?.last4Number ? `Card ends ${card.last4Number} has been ${nextStatus}!` : `Card has been ${nextStatus}!`)
      );
      setHideVisibility(true);
    },
    [card?.last4Number, handleSetLockUnlockSuccessMessage, refetch, refetchCardList, t]
  );

  const handleCloseLockUnlockSuccessModal = useCallback(() => {
    setHideVisibility(false);
    handleResetStores();
  }, [handleResetStores]);

  const handleOpenCardReplacementModal = useCallback(() => {
    setCardInfo();
    handleResetStores();
    handleChangeVisibility(true);
  }, [handleChangeVisibility, handleResetStores, setCardInfo]);

  const handleCloseErrorModal = useCallback(
    (value: boolean) => {
      handleAddPermanentSnackbar({
        text: t(card?.last4Number ? `Card ends ${card.last4Number} was closed!` : 'Card was closed!'),
        closeIcon: true,
      });
      setCloseCardSuccess(value);
      setHideVisibility(false);
      handleResetStores();
    },
    [card?.last4Number, handleAddPermanentSnackbar, handleResetStores, t]
  );

  const handleChangeStatusMutationHandler = useCallback(
    async (status: FiservCardStatusEnum) => {
      if (card) {
        await changeStatusMutation({ id: cardId, status });
        refetch();
        refetchCardList();
      }
    },
    [card, cardId, changeStatusMutation, refetch, refetchCardList]
  );

  useEffect(() => {
    handleToggleCardLock(
      card?.status === FiservCardStatusEnum.FROZEN || card?.status === FiservCardStatusEnum.PENDING_FROZEN
    );
    if (card?.cardPurses) handleSetLockedPurses(card.cardPurses);
  }, [card?.cardPurses, card?.status, handleResetAccessModal, handleToggleCardLock, handleSetLockedPurses]);

  if (!card) {
    return null;
  }

  const modalContent = (
    <>
      {!isMobile && <Box height={{ min: isCardFlipDisabled ? '94px' : '42px' }} />}
      <Box
        background="module"
        round="container1Round"
        align="center"
        justify="end"
        width={isMobile ? '100%' : undefined}
        pad={{ bottom: isMobile ? undefined : 'spacing24' }}
      >
        <CardModalWrapper
          isLoading={isLoading}
          card={card}
          isEmployeeLimitedAccess={isEmployeeLimitedAccess}
        />
        <Box align="center" gap={isMobile ? 'spacing16' : 'spacing24'} width={isMobile ? '100%' : undefined}>
          <Text
            weight="bold"
            size="xxlarge"
            color="textBody"
          >
            {t('What Would You Like to Do?')}
          </Text>
          <CardActionButtons
            card={card}
            loadingButton={loadingButton}
            onOpenLockModal={handleChangeCardAccessModalVisible}
            onOpenLockUnlockCardModal={handleOpenLockUnlockCardModal}
            onOpenCardReplacementModal={handleOpenCardReplacementModal}
            onOpenCloseConfirmationModal={handleOpenCloseModal}
            onOpenManageAccountAccessModal={handleOpenManageAccountAccessModal}
          />
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <Modal
        visible={visible}
        onSetVisible={handleResetStores}
        testId="change-status-card-modal"
        isCloseButtonHidden={isMobile}
        isFullScreen={isMobile}
        contentMobileStyle={{ padding: 0 }}
      >
        {isMobile ? (
          <>
            <Box margin={{ vertical: 'spacing12' }}>
              <RawButton onClick={handleResetStores} data-testId="change-status-card-modal_back-button">
                <Box direction="row" align="center" height="40px" pad={{ horizontal: 'spacing8' }}>
                  <Box margin={{ left: 'spacing10' }}>
                    <NavigationLeftIcon color="textAccent" size="20px" />
                  </Box>
                  <Text color="textAccent" size="14px" weight={700} margin={{ left: 'spacing10' }}>
                    {t('Manage Card Access')}
                  </Text>
                </Box>
              </RawButton>
            </Box>
            <Box pad="spacing20" background="module" round="container3Round">
              {modalContent}
            </Box>
          </>
        ) : (
          modalContent
        )}
      </Modal>
      <CloseCardConfirmationModal
        header="Are You Sure That You Want to Close This Card?"
        helpText="You cannot reopen this card after closing it"
        buttonText="Confirm"
        visible={closeCardConfirmation}
        onSetVisible={() => {
          setCloseCardConfirmation(false);
          setHideVisibility(false);
        }}
        onCancel={() => {
          setCloseCardConfirmation(false);
          setHideVisibility(false);
        }}
        submitting={changeStatusMutationLoading}
        onSubmit={async () => {
          await handleChangeStatusMutationHandler(FiservCardStatusEnum.CLOSED);
          setCloseCardConfirmation(false);
          setCloseCardSuccess(true);
          setHideVisibility(true);
        }}
      />
      <ErrorModal
        visible={closeCardSuccess}
        onSetVisible={handleCloseErrorModal}
        header={card?.last4Number ? `Card Ends ${card.last4Number} Was Closed!` : 'Card Was Closed!'}
        buttonText="Got It!"
      />
      <CardReplaceModal
        visible={modalVisible}
        cardholderName={cardholderName}
        onSetVisible={handleChangeVisibility}
        refreshCardList={refetchCardList}
        replacementInitiationType={ReplacementInitiationType.CARDS_PAGE}
      />
      {isCardAccessModalVisible && (
        <CardAccessModal
          card={card}
          visible={isCardAccessModalVisible}
          onChangeCardAccessModalVisible={handleChangeCardAccessModalVisible}
          onHideVisibility={setHideVisibility}
          refreshCardList={refetchCardList}
        />
      )}
      {isLockUnlockCardModalVisible && (
        <LockUnlockCardModal
          card={card}
          visible={isLockUnlockCardModalVisible}
          onCancel={handleCloseLockUnlockCardModal}
          onSuccess={handleLockUnlockCardSuccess}
        />
      )}
      {isManageAccountAccessModalVisible && (
        <ManageAccountAccessModal
          employeeId={user?.employee?.id}
          visible={isManageAccountAccessModalVisible}
          onCancel={handleCloseManageAccountAccessModal}
          onSuccess={handleManageAccountAccessSuccess}
        />
      )}
      <SuccessModal
        testId="lock-unlock-card-success-modal"
        visible={!!lockUnlockSuccessMessage}
        onSetVisible={handleCloseLockUnlockSuccessModal}
        header={lockUnlockSuccessMessage || ''}
        buttonText={t('Got it!')}
      />
    </>
  );
};
